import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useApp } from "./context";
import { t } from "./i18n";

function makeIcon(active = false) {
  return L.divIcon({
    html: `<div class="stilet-marker ${active ? 'active' : ''}"></div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 14, { duration: 1.0 });
  }, [position, map]);
  return null;
}

export default function ListingsPage() {
  const { lang, API } = useApp();
  const [apartments, setApartments] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);

  const [location, setLocation] = useState("all");
  const [guests, setGuests] = useState("any");
  const [maxPrice, setMaxPrice] = useState(400);

  useEffect(() => {
    axios.get(`${API}/apartments`).then(r => setApartments(r.data));
  }, [API]);

  const locations = useMemo(() => {
    const set = new Set(apartments.map(a => lang === "en" ? a.location_name_en : a.location_name_sr));
    return Array.from(set);
  }, [apartments, lang]);

  const filtered = apartments.filter(a => {
    const loc = lang === "en" ? a.location_name_en : a.location_name_sr;
    if (location !== "all" && loc !== location) return false;
    if (guests !== "any" && a.max_guests < parseInt(guests)) return false;
    if (a.price_per_night > maxPrice) return false;
    return true;
  });

  const center = apartments.length > 0
    ? [apartments[0].lat, apartments[0].lng]
    : [42.4317, 18.7522]; // Kotor Bay fallback

  return (
    <div className="section-pad" data-testid="page-listings">
      <div className="container-x">
        <div className="overline mb-3">Stilet · Kotor</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">{t("listings.title", lang)}</h1>

        {/* Filters */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4 p-5" style={{ background: 'var(--bg-secondary)' }} data-testid="filters">
          <div>
            <label className="field-label">{t("listings.filter_location", lang)}</label>
            <select className="input-base" value={location} onChange={e => setLocation(e.target.value)} data-testid="filter-location">
              <option value="all">{t("listings.filter_all", lang)}</option>
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">{t("listings.filter_guests", lang)}</label>
            <select className="input-base" value={guests} onChange={e => setGuests(e.target.value)} data-testid="filter-guests">
              <option value="any">{t("listings.filter_all", lang)}</option>
              {[1,2,3,4,5,6].map(g => <option key={g} value={g}>{g}+</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="field-label">{t("listings.filter_max_price", lang)} — €{maxPrice}</label>
            <input type="range" min={50} max={400} step={10} value={maxPrice} onChange={e => setMaxPrice(parseInt(e.target.value))} className="w-full" data-testid="filter-price" />
          </div>
        </div>

        {/* Map + List */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 space-y-10" data-testid="listings-grid">
            {filtered.length === 0 && (
              <p style={{ color: 'var(--text-muted)' }} data-testid="no-results">{t("listings.no_results", lang)}</p>
            )}
            {filtered.map(apt => (
              <div key={apt.id}
                className="listing-card grid grid-cols-1 md:grid-cols-5 gap-5 group"
                onMouseEnter={() => { setHoverId(apt.id); setFlyTarget([apt.lat, apt.lng]); }}
                onMouseLeave={() => setHoverId(null)}
                data-testid={`listing-${apt.slug}`}>
                <Link to={`/apartments/${apt.slug}`} className="md:col-span-2 block">
                  <div className="listing-img-wrap" style={{ aspectRatio: '4/3' }}>
                    <img src={apt.images[0]} alt={apt.name} className="listing-img" />
                  </div>
                </Link>
                <div className="md:col-span-3 flex flex-col justify-between py-2">
                  <div>
                    <div className="overline">{lang === "en" ? apt.location_name_en : apt.location_name_sr}</div>
                    <Link to={`/apartments/${apt.slug}`}>
                      <h3 className="font-display text-3xl mt-2">{apt.name}</h3>
                    </Link>
                    <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {lang === "en" ? apt.short_description_en : apt.short_description_sr}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="amenity-pill">{apt.rooms} {t("details.rooms", lang)}</span>
                      <span className="amenity-pill">{apt.beds} {t("details.beds", lang)}</span>
                      <span className="amenity-pill">{apt.size_sqm} {t("details.sqm", lang)}</span>
                      <span className="amenity-pill">{apt.max_guests} {t("listings.filter_guests", lang)}</span>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <span><span className="font-display text-3xl">€{apt.price_per_night}</span> <span style={{ color: 'var(--text-muted)' }}>{t("listings.night", lang)}</span></span>
                    <Link to={`/apartments/${apt.slug}`} className="btn-link" data-testid={`view-${apt.slug}`}>{t("listings.view_details", lang)} →</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className="overline mb-3">{t("listings.map_title", lang)}</div>
            <div style={{ height: 560, border: '1px solid var(--border)' }} data-testid="map-wrap">
              {apartments.length > 0 && (
              <MapContainer key={`${center[0]}-${center[1]}`} center={center} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
                <FlyTo position={flyTarget} />
                {filtered.map(apt => (
                  <Marker
                    key={apt.id}
                    position={[apt.lat, apt.lng]}
                    icon={makeIcon(hoverId === apt.id)}
                    eventHandlers={{
                      mouseover: () => setHoverId(apt.id),
                      mouseout: () => setHoverId(null),
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: 180 }}>
                        <div className="overline" style={{fontSize:'0.65rem'}}>{lang === "en" ? apt.location_name_en : apt.location_name_sr}</div>
                        <div className="font-display" style={{ fontSize: '1.2rem', marginTop: 4 }}>{apt.name}</div>
                        <div style={{ marginTop: 6, fontSize: '0.85rem' }}>€{apt.price_per_night} {t("listings.night", lang)}</div>
                        <Link to={`/apartments/${apt.slug}`} style={{ display:'inline-block', marginTop: 8, borderBottom: '1px solid var(--text-primary)', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform:'uppercase' }}>
                          {t("listings.view_details", lang)}
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
